// Phase 1 core product catalog — the 6 primary products customers buy.
// The 27 /services entries remain the detailed secondary catalog.

export const products = [
  {
    id: 'compute',
    name: 'Cloud Compute',
    shortName: 'Compute',
    tagline: 'Virtual machines, GPU and dedicated capacity',
    navDesc: 'VMs, GPU, bare metal & dedicated cloud',
    description: 'Windows and Linux virtual machines with high availability, live migration and hot-add CPU/RAM — plus GPU compute, bare metal servers and fully dedicated private stacks for regulated workloads.',
    requestCta: 'Request Compute',
    capabilities: [
      { title: 'Virtual Machines', desc: 'Up to 64 vCPU and 512 GB RAM per VM, with HA restart on host failure, live migration and a browser-based console.' },
      { title: 'GPU Compute', desc: 'Passthrough GPUs for training and rendering, or vGPU slices for virtual desktops and inference workloads.' },
      { title: 'Bare Metal', desc: 'Dedicated physical servers up to 128 cores and 2 TB RAM, provisioned in under 20 minutes and joined to your VPCs.' },
      { title: 'Dedicated Cloud', desc: 'A physically isolated stack of the DAAKYI platform for one organization — in a DAAKYI region or on your premises.' },
      { title: 'High Availability', desc: 'Automatic restart of protected VMs on healthy hosts, with anti-affinity rules for clustered applications.' },
      { title: 'Right-Sized Billing', desc: 'Compute is metered only while powered on. Powered-off VMs stop accruing compute charges.' }
    ],
    useCases: [
      { title: 'Enterprise applications', desc: 'ERP, core business systems and databases on VMs with HA, snapshots and independent backup.' },
      { title: 'VMware replacement', desc: 'Move existing vSphere estates with agentless V2V migration and minutes of cutover downtime.' },
      { title: 'AI & rendering', desc: 'GPU instances for model training, inference and media rendering on in-region infrastructure.' }
    ],
    serviceIds: ['dc-compute-instances', 'dc-gpu-compute', 'dc-bare-metal', 'dc-dedicated-cloud'],
    docsPath: '/docs/virtual-machines/vm-overview'
  },
  {
    id: 'storage',
    name: 'Cloud Storage',
    shortName: 'Storage',
    tagline: 'Block, file, object and archive storage',
    navDesc: 'Block, file, object & archive',
    description: 'Replicated block storage in performance tiers, NFS and SMB file shares, S3-compatible object storage and a cold archive tier — all encrypted at rest with snapshot support.',
    requestCta: 'Request Cloud Access',
    capabilities: [
      { title: 'Block Storage', desc: 'Thin-provisioned virtual disks with 2–3 replicas, online expansion and High Performance, Performance and Capacity tiers.' },
      { title: 'Object Storage', desc: 'S3-compatible buckets that work unchanged with the AWS CLI, boto3 and rclone — with versioning and pre-signed URLs.' },
      { title: 'File Storage', desc: 'NFS and SMB shares scoped to your VPC, with Active Directory integration for enterprise permissions.' },
      { title: 'Archive Storage', desc: 'Capacity-optimized cold tier with WORM vault lock and automatic tiering for long-term retention.' },
      { title: 'Snapshots', desc: 'Point-in-time, crash-consistent snapshots of disks and VMs, restorable in place or as new volumes.' },
      { title: 'Encryption at Rest', desc: 'All storage classes are encrypted at rest, with customer-managed keys available through key management.' }
    ],
    useCases: [
      { title: 'Application data', desc: 'Fast replicated disks for databases and transactional systems, sized to workload IO profiles.' },
      { title: 'Backup targets & media', desc: 'Object and file repositories for backup data, media libraries and analytics datasets.' },
      { title: 'Regulated retention', desc: 'WORM-locked archive storage for records that must be kept immutable for defined periods.' }
    ],
    serviceIds: ['dc-block-storage', 'dc-object-storage', 'dc-file-storage', 'dc-archive-storage'],
    docsPath: '/docs/storage/block-storage'
  },
  {
    id: 'networking',
    name: 'Cloud Networking',
    shortName: 'Networking',
    tagline: 'Private networks, connectivity and traffic management',
    navDesc: 'VPC, load balancing, VPN & private links',
    description: 'Isolated Virtual Private Clouds with distributed routing, L4/L7 load balancing, site-to-site and client VPN, dedicated private circuits and micro-segmented firewalling at every VM interface.',
    requestCta: 'Request Cloud Access',
    capabilities: [
      { title: 'Virtual Private Cloud', desc: 'Isolated networks with subnets, a distributed virtual router, NAT, elastic IPs, security groups and peering.' },
      { title: 'Load Balancing', desc: 'L4/L7 application delivery with health checks, SSL termination and session persistence.' },
      { title: 'VPN Connectivity', desc: 'Site-to-site IPSec (IKEv2) tunnels and client SSL VPN with multi-factor authentication.' },
      { title: 'Direct Connect', desc: 'Dedicated 1/10 Gbps private circuits into DAAKYI Cloud with BGP routing and MACsec encryption.' },
      { title: 'Distributed Firewall', desc: 'Micro-segmentation enforced at every VM interface with tag-based policies and one-click quarantine.' },
      { title: 'Hybrid Topologies', desc: 'Connect on-premises facilities privately and run hybrid during and after migration.' }
    ],
    useCases: [
      { title: 'Multi-tier applications', desc: 'Segmented web, application and database tiers with load balancing and per-tier firewall policy.' },
      { title: 'Branch & remote access', desc: 'Site-to-site VPN for offices and client VPN with MFA for remote engineering teams.' },
      { title: 'Private interconnect', desc: 'Deterministic, private connectivity between your data center and DAAKYI regions.' }
    ],
    serviceIds: ['dc-virtual-network', 'dc-load-balancer', 'dc-cloud-vpn', 'dc-direct-connect', 'dc-network-firewall'],
    docsPath: '/docs/networking/vpc'
  },
  {
    id: 'cloud-security',
    name: 'Cloud Security',
    shortName: 'Cloud Security',
    tagline: 'Identity, protection and continuous threat monitoring',
    navDesc: 'SOC/XDR, WAF, DDoS, IAM & KMS',
    description: 'Layered security operated as part of the platform: identity and access management, web application firewall, DDoS protection, key management, tamper-evident audit trails and a security operations capability with XDR correlation.',
    requestCta: 'Request Cloud Access',
    capabilities: [
      { title: 'Security Operations (SOC/XDR)', desc: 'Continuous telemetry collection, XDR correlation, analyst triage and containment through quarantine.' },
      { title: 'Web Application Firewall', desc: 'HTTP inspection against SQL injection, XSS, bots and abuse, with a detection-to-prevention rollout path.' },
      { title: 'DDoS Protection', desc: 'Always-on detection and traffic scrubbing to keep public-facing services available under attack.' },
      { title: 'Identity & Access', desc: 'Named users, custom roles, MFA, source-IP restrictions and SAML/OIDC single sign-on federation.' },
      { title: 'Key Management', desc: 'Customer-managed encryption keys with rotation, backed by hardware security modules.' },
      { title: 'Audit Trails', desc: 'Every console and API action logged with actor, source IP and result — tamper-evident and exportable.' }
    ],
    useCases: [
      { title: 'Regulated workloads', desc: 'Meet sector security standards with encryption, segmentation, audit evidence and managed detection.' },
      { title: 'Public-facing applications', desc: 'WAF and DDoS protection in front of customer portals, APIs and e-commerce platforms.' },
      { title: 'Zero-trust segmentation', desc: 'Micro-segment east-west traffic and enforce least-privilege access across the estate.' }
    ],
    serviceIds: ['dc-security-center', 'dc-waf', 'daakyi-shield', 'dc-kms', 'dc-iam', 'dc-audit-trails'],
    docsPath: '/docs/security/distributed-firewall'
  },
  {
    id: 'backup-dr',
    name: 'Backup & Disaster Recovery',
    shortName: 'Backup & DR',
    tagline: 'Data protection and business continuity',
    navDesc: 'Backup, CDP & cross-site DR',
    description: 'Scheduled incremental-forever backup to independent immutable repositories, continuous data protection with recovery points seconds apart, and cross-site disaster recovery with tested, dependency-ordered recovery plans.',
    requestCta: 'Request Cloud Access',
    capabilities: [
      { title: 'Scheduled Backup', desc: 'Incremental-forever backups to an independent, immutable repository with VM, disk and file-level restore.' },
      { title: 'Continuous Data Protection', desc: 'Every write journaled, with recovery points seconds apart inside a 24–72 hour window.' },
      { title: 'Cross-Site DR', desc: 'Asynchronous replication with RPO measured in minutes and orchestrated failover to a second region.' },
      { title: 'Recovery Plans', desc: 'Dependency-ordered runbooks that bring systems back in the right sequence, not all at once.' },
      { title: 'Non-Disruptive Drills', desc: 'Isolated DR testing that proves recoverability without touching production workloads.' },
      { title: 'Immutable Repositories', desc: 'Backup data held outside production failure domains and protected against tampering.' }
    ],
    useCases: [
      { title: 'Ransomware resilience', desc: 'Immutable backups and second-scale CDP recovery points to roll back past an encryption event.' },
      { title: 'Business continuity', desc: 'Cross-site recovery for systems where downtime carries regulatory or revenue consequences.' },
      { title: 'Audit-ready recovery', desc: 'Documented, rehearsed recovery plans with drill evidence for regulators and boards.' }
    ],
    serviceIds: ['dc-backup', 'dc-cdp', 'dc-disaster-recovery'],
    docsPath: '/docs/backup-dr/backup-policies'
  },
  {
    id: 'managed-cloud',
    name: 'Managed Cloud',
    shortName: 'Managed Cloud',
    tagline: 'DAAKYI engineers operate your environment',
    navDesc: 'Managed operations, migration & support',
    description: 'DAAKYI engineers design, migrate, operate and secure your cloud environment — from managed migration off VMware and physical servers to day-2 operations, monitoring, patching and dedicated private environments.',
    requestCta: 'Talk to a Cloud Architect',
    capabilities: [
      { title: 'Managed Migration', desc: 'Agentless VMware (V2V) and physical server (P2V) migration planned and executed by DAAKYI engineers.' },
      { title: 'Managed Operations', desc: 'Day-2 operations — patching, capacity, incident response — handled against agreed service levels.' },
      { title: 'Dedicated Environments', desc: 'Private, physically isolated stacks operated by DAAKYI, in-region or on your premises.' },
      { title: 'Monitoring & Alarms', desc: 'Per-VM, disk, load balancer and tunnel metrics with alerting into your incident tooling.' },
      { title: 'Architecture Reviews', desc: 'Cloud architects design and review landing zones, network topologies and recovery strategies.' },
      { title: 'In-Region Support', desc: 'Engineers in-region with accountable response times, not a distant ticket queue.' }
    ],
    useCases: [
      { title: 'Lean IT teams', desc: 'Run enterprise infrastructure without building a full cloud operations team in-house.' },
      { title: 'Sovereign private cloud', desc: 'A dedicated environment in your jurisdiction, operated to enterprise standards by DAAKYI.' },
      { title: 'Complex migrations', desc: 'Multi-wave datacenter exits planned, rehearsed and executed with rollback options.' }
    ],
    serviceIds: ['dc-dedicated-cloud', 'dc-vmware-migration', 'dc-physical-migration', 'dc-cloud-console', 'dc-monitoring'],
    docsPath: '/docs/migration/migration-planning'
  }
];

export const productById = Object.fromEntries(products.map((p) => [p.id, p]));
